/**
 * 禁府系统状态管理
 */

import { create } from 'zustand'
import type {
  SecretPoliceState,
  Prisoner,
  Spy,
  Intelligence,
  Prison,
  SpyMission,
  SecretPoliceEvent,
  IntelligenceType,
  IntelligenceLevel,
  SpyMissionType
} from '../types/secretPolice'

// 初始状态
const initialState: SecretPoliceState = {
  prisoners: new Map(),
  spies: new Map(),
  intelligence: new Map(),
  prisons: new Map(),
  spyMissions: new Map(),
  events: new Map(),
  statistics: {
    totalPrisoners: 0,
    totalSpies: 0,
    totalIntelligence: 0,
    totalPrisons: 0,
    totalSpyMissions: 0,
    successfulMissions: 0,
    failedMissions: 0
  }
}

// 预设监狱
const presetPrisons: Omit<Prison, 'id' | 'builtAt'>[] = [
  {
    name: '镐京大狱',
    cityId: 'city_gaojing',
    factionId: 'faction_zhou',
    facilities: [
      {
        type: 'detention_cell',
        capacity: 20,
        level: 1
      },
      {
        type: 'prison_cell',
        capacity: 50,
        level: 1
      },
      {
        type: 'interrogation_room',
        capacity: 5,
        level: 1
      },
      {
        type: 'execution_ground',
        capacity: 1,
        level: 1
      }
    ],
    prisoners: [],
    guards: [],
    securityLevel: 5,
    status: 'operating'
  }
]

interface SecretPoliceStore extends SecretPoliceState {
  // 囚犯管理
  capturePrisoner: (characterId: string, name: string, factionId: string, capturedFactionId: string, prisonId: string) => string
  interrogatePrisoner: (prisonerId: string) => void
  tryPrisoner: (prisonerId: string, sentence: Prisoner['sentence']) => void
  executePrisoner: (prisonerId: string) => void
  releasePrisoner: (prisonerId: string) => void
  
  // 间谍管理
  deploySpy: (characterId: string, name: string, factionId: string, targetFactionId: string, coverIdentity: Spy['coverIdentity']) => string
  recallSpy: (spyId: string) => void
  
  // 情报管理
  gatherIntelligence: (title: string, type: IntelligenceType, level: IntelligenceLevel, sourceFactionId: string, targetFactionId: string, content: string, source: Intelligence['source']) => string
  analyzeIntelligence: (intelligenceId: string) => void
  
  // 监狱管理
  buildPrison: (name: string, cityId: string, factionId: string) => string
  upgradePrison: (prisonId: string) => void
  
  // 间谍任务管理
  assignSpyMission: (name: string, type: SpyMissionType, spyId: string, targetFactionId: string, target: SpyMission['target']) => string
  completeSpyMission: (missionId: string, success: boolean, details: string) => void

  // 事件管理
  createEvent: (type: SecretPoliceEvent['type'], description: string, details: Record<string, any>) => void

  // 统计
  updateStatistics: () => void

  // 初始化
  init: () => void
}

export const useSecretPoliceStore = create<SecretPoliceStore>((set, get) => ({
  ...initialState,
  
  // 初始化
  init: () => {
    set(state => {
      const newPrisons = new Map<string, Prison>()
      
      // 初始化预设监狱
      presetPrisons.forEach(prison => {
        const id = `prison_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const now = { year: 1046, month: 1, day: 1 }
        
        newPrisons.set(id, {
          ...prison,
          id,
          builtAt: now
        })
      })
      
      return {
        prisons: newPrisons,
        statistics: {
          ...state.statistics,
          totalPrisons: newPrisons.size
        }
      }
    })
  },
  
  // 囚犯管理
  capturePrisoner: (characterId, name, factionId, capturedFactionId, prisonId) => {
    const id = `prisoner_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newPrisoners = new Map(state.prisoners)
      newPrisoners.set(id, {
        id,
        characterId,
        name,
        factionId,
        capturedFactionId,
        status: 'detained',
        crimes: [],
        sentence: {
          type: 'imprisonment'
        },
        interrogation: {
          informationGathered: [],
          resistance: 50,
          broken: false
        },
        prisonId,
        cellId: `cell_${Date.now()}`,
        capturedAt: now,
        escapeAttempts: 0,
        successfulEscape: false
      })
      
      // 更新监狱囚犯列表
      const newPrisons = new Map(state.prisons)
      const prison = newPrisons.get(prisonId)
      if (prison) {
        newPrisons.set(prisonId, {
          ...prison,
          prisoners: [...prison.prisoners, id]
        })
      }
      
      return {
        prisoners: newPrisoners,
        prisons: newPrisons,
        statistics: {
          ...state.statistics,
          totalPrisoners: state.statistics.totalPrisoners + 1
        }
      }
    })
    
    return id
  },
  
  interrogatePrisoner: (prisonerId) => {
    set(state => {
      const newPrisoners = new Map(state.prisoners)
      const prisoner = newPrisoners.get(prisonerId)
      
      if (prisoner) {
        const newResistance = Math.max(0, prisoner.interrogation.resistance - 20)
        const broken = newResistance === 0
        
        newPrisoners.set(prisonerId, {
          ...prisoner,
          interrogation: {
            ...prisoner.interrogation,
            resistance: newResistance,
            broken,
            informationGathered: broken 
              ? [...prisoner.interrogation.informationGathered, '重要情报']
              : prisoner.interrogation.informationGathered
          }
        })
        
        return { prisoners: newPrisoners }
      }
      
      return state
    })
  },
  
  tryPrisoner: (prisonerId, sentence) => {
    set(state => {
      const newPrisoners = new Map(state.prisoners)
      const prisoner = newPrisoners.get(prisonerId)
      
      if (prisoner) {
        newPrisoners.set(prisonerId, {
          ...prisoner,
          status: 'trialed',
          sentence,
          trialAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { prisoners: newPrisoners }
      }
      
      return state
    })
  },
  
  executePrisoner: (prisonerId) => {
    set(state => {
      const newPrisoners = new Map(state.prisoners)
      const prisoner = newPrisoners.get(prisonerId)
      
      if (prisoner) {
        newPrisoners.set(prisonerId, {
          ...prisoner,
          status: 'executed',
          executedAt: { year: 1046, month: 1, day: 1 }
        })
        
        // 更新监狱囚犯列表
        const newPrisons = new Map(state.prisons)
        const prison = newPrisons.get(prisoner.prisonId)
        if (prison) {
          newPrisons.set(prison.id, {
            ...prison,
            prisoners: prison.prisoners.filter(id => id !== prisonerId)
          })
        }
        
        return {
          prisoners: newPrisoners,
          prisons: newPrisons
        }
      }
      
      return state
    })
  },
  
  releasePrisoner: (prisonerId) => {
    set(state => {
      const newPrisoners = new Map(state.prisoners)
      const prisoner = newPrisoners.get(prisonerId)
      
      if (prisoner) {
        newPrisoners.set(prisonerId, {
          ...prisoner,
          status: 'released',
          releasedAt: { year: 1046, month: 1, day: 1 }
        })
        
        // 更新监狱囚犯列表
        const newPrisons = new Map(state.prisons)
        const prison = newPrisons.get(prisoner.prisonId)
        if (prison) {
          newPrisons.set(prison.id, {
            ...prison,
            prisoners: prison.prisoners.filter(id => id !== prisonerId)
          })
        }
        
        return {
          prisoners: newPrisoners,
          prisons: newPrisons
        }
      }
      
      return state
    })
  },
  
  // 间谍管理
  deploySpy: (characterId, name, factionId, targetFactionId, coverIdentity) => {
    const id = `spy_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newSpies = new Map(state.spies)
      newSpies.set(id, {
        id,
        characterId,
        name,
        factionId,
        targetFactionId,
        status: 'infiltrating',
        coverIdentity,
        skills: {
          stealth: 70,
          deception: 80,
          combat: 50,
          intelligence: 90,
          sabotage: 60
        },
        loyalty: 80,
        history: {
          missions: [],
          intelligenceGathered: [],
          closeCalls: 0
        },
        deployedAt: now,
        communicationMethod: '秘密信件',
        exposureRisk: 20
      })
      
      return {
        spies: newSpies,
        statistics: {
          ...state.statistics,
          totalSpies: state.statistics.totalSpies + 1
        }
      }
    })
    
    return id
  },
  
  recallSpy: (spyId) => {
    set(state => {
      const newSpies = new Map(state.spies)
      const spy = newSpies.get(spyId)
      
      if (spy) {
        newSpies.set(spyId, {
          ...spy,
          status: 'extracted',
          extractedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { spies: newSpies }
      }
      
      return state
    })
  },
  
  // 情报管理
  gatherIntelligence: (title, type, level, sourceFactionId, targetFactionId, content, source) => {
    const id = `intelligence_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newIntelligence = new Map(state.intelligence)
      newIntelligence.set(id, {
        id,
        title,
        type,
        level,
        sourceFactionId,
        targetFactionId,
        content,
        status: 'raw',
        source,
        gatheredAt: now,
        value: level === 'critical' ? 100 : level === 'high' ? 75 : level === 'medium' ? 50 : 25,
        impact: {}
      })
      
      return {
        intelligence: newIntelligence,
        statistics: {
          ...state.statistics,
          totalIntelligence: state.statistics.totalIntelligence + 1
        }
      }
    })
    
    return id
  },
  
  analyzeIntelligence: (intelligenceId) => {
    set(state => {
      const newIntelligence = new Map(state.intelligence)
      const intelligence = newIntelligence.get(intelligenceId)
      
      if (intelligence) {
        newIntelligence.set(intelligenceId, {
          ...intelligence,
          status: 'analyzed',
          analyzedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { intelligence: newIntelligence }
      }
      
      return state
    })
  },
  
  // 监狱管理
  buildPrison: (name, cityId, factionId) => {
    const id = `prison_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newPrisons = new Map(state.prisons)
      newPrisons.set(id, {
        id,
        name,
        cityId,
        factionId,
        facilities: [
          {
            type: 'detention_cell',
            capacity: 10,
            level: 1
          },
          {
            type: 'prison_cell',
            capacity: 20,
            level: 1
          }
        ],
        prisoners: [],
        guards: [],
        securityLevel: 3,
        status: 'operating',
        builtAt: now
      })
      
      return {
        prisons: newPrisons,
        statistics: {
          ...state.statistics,
          totalPrisons: state.statistics.totalPrisons + 1
        }
      }
    })
    
    return id
  },
  
  upgradePrison: (prisonId) => {
    set(state => {
      const newPrisons = new Map(state.prisons)
      const prison = newPrisons.get(prisonId)
      
      if (prison) {
        newPrisons.set(prisonId, {
          ...prison,
          securityLevel: Math.min(10, prison.securityLevel + 1),
          facilities: prison.facilities.map(facility => ({
            ...facility,
            level: Math.min(5, facility.level + 1),
            capacity: facility.capacity + 5
          })),
          lastUpgradedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { prisons: newPrisons }
      }
      
      return state
    })
  },
  
  // 间谍任务管理
  assignSpyMission: (name, type, spyId, targetFactionId, target) => {
    const id = `spy_mission_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newSpyMissions = new Map(state.spyMissions)
      newSpyMissions.set(id, {
        id,
        name,
        type,
        spyId,
        targetFactionId,
        target,
        difficulty: 5,
        rewards: {
          money: 1000,
          experience: 100,
          intelligence: []
        },
        risks: {
          capture: 30,
          death: 20,
          exposure: 40
        },
        status: 'planned',
        startedAt: now,
        estimatedCompletion: { year: 1046, month: 1, day: 15 },
        result: {
          success: false,
          details: '',
          intelligenceGathered: [],
          casualties: 0
        }
      })
      
      // 更新间谍状态
      const newSpies = new Map(state.spies)
      const spy = newSpies.get(spyId)
      if (spy) {
        newSpies.set(spyId, {
          ...spy,
          status: 'active',
          currentMissionId: id
        })
      }
      
      return {
        spyMissions: newSpyMissions,
        spies: newSpies,
        statistics: {
          ...state.statistics,
          totalSpyMissions: state.statistics.totalSpyMissions + 1
        }
      }
    })
    
    return id
  },
  
  completeSpyMission: (missionId, success, details) => {
    set(state => {
      const newSpyMissions = new Map(state.spyMissions)
      const mission = newSpyMissions.get(missionId)
      
      if (mission) {
        newSpyMissions.set(missionId, {
          ...mission,
          status: success ? 'completed' : 'failed',
          completedAt: { year: 1046, month: 1, day: 1 },
          result: {
            success,
            details,
            intelligenceGathered: success ? ['敌方军事部署'] : [],
            casualties: success ? 0 : 1
          }
        })
        
        // 更新间谍状态
        const newSpies = new Map(state.spies)
        const spy = newSpies.get(mission.spyId)
        if (spy) {
          newSpies.set(mission.spyId, {
            ...spy,
            status: success ? 'active' : 'compromised',
            currentMissionId: undefined,
            history: {
              ...spy.history,
              missions: [...spy.history.missions, missionId],
              intelligenceGathered: success 
                ? [...spy.history.intelligenceGathered, '敌方军事部署']
                : spy.history.intelligenceGathered
            }
          })
        }
        
        const successfulMissions = success ? state.statistics.successfulMissions + 1 : state.statistics.successfulMissions
        const failedMissions = !success ? state.statistics.failedMissions + 1 : state.statistics.failedMissions
        
        return {
          spyMissions: newSpyMissions,
          spies: newSpies,
          statistics: {
            ...state.statistics,
            successfulMissions,
            failedMissions
          }
        }
      }
      
      return state
    })
  },
  
  // 事件管理
  createEvent: (type, description, details) => {
    const id = `event_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newEvents = new Map(state.events)
      newEvents.set(id, {
        id,
        type,
        description,
        date: now,
        details
      })
      
      return { events: newEvents }
    })
  },
  
  // 统计
  updateStatistics: () => {
    const state = get()
    let totalPrisoners = 0
    let totalSpies = 0
    let totalIntelligence = 0
    let totalSpyMissions = 0
    let successfulMissions = 0
    let failedMissions = 0
    
    Array.from(state.prisoners.values()).forEach(prisoner => {
      if (prisoner.status !== 'released' && prisoner.status !== 'executed') {
        totalPrisoners++
      }
    })
    
    Array.from(state.spies.values()).forEach(spy => {
      if (spy.status !== 'extracted' && spy.status !== 'dead') {
        totalSpies++
      }
    })
    
    totalIntelligence = state.intelligence.size
    totalSpyMissions = state.spyMissions.size
    
    Array.from(state.spyMissions.values()).forEach(mission => {
      if (mission.status === 'completed') successfulMissions++
      if (mission.status === 'failed') failedMissions++
    })
    
    set({
      statistics: {
        totalPrisoners,
        totalSpies,
        totalIntelligence,
        totalPrisons: state.prisons.size,
        totalSpyMissions,
        successfulMissions,
        failedMissions
      }
    })
  }
}))

// 初始化禁府系统
useSecretPoliceStore.getState().init()
